import axios from 'axios';

// Use environment variable or fallback to empty string (relative URL for nginx proxy)
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to attach auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Callback for notifying AuthContext when tokens are force-cleared
let onForceLogout = null;

/**
 * Register a callback that fires when the interceptor clears tokens
 * (e.g. refresh failed / session expired). AuthContext uses this to
 * update React state so ProtectedRoute can redirect properly.
 */
export const setForceLogoutHandler = (handler) => {
  onForceLogout = handler;
};

/**
 * Returns the currently registered force-logout handler (or null).
 * Used by `utils/authFetch.js` so legacy raw-fetch call sites can share
 * the same logout signalling pipeline as the axios interceptor.
 */
export const getForceLogoutHandler = () => onForceLogout;

// Token refresh state to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// Add response interceptor for automatic token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 500) {
      console.error('Server error:', error.response.data);
    }

    // If 401 and not already retrying, attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh on login or refresh endpoints
      if (originalRequest.url?.includes('/proxy-login') ||
          originalRequest.url?.includes('/accounts/refresh')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request until refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        isRefreshing = false;
        return Promise.reject(error);
      }

      try {
        const response = await api.post('/api/accounts/refresh/', { refresh: refreshToken });
        const newAccess = response.data.access_token || response.data.access;
        const newRefresh = response.data.refresh_token || response.data.refresh;

        if (newAccess) {
          localStorage.setItem('accessToken', newAccess);
          api.defaults.headers.common['Authorization'] = `Bearer ${newAccess}`;
        }
        if (newRefresh) {
          localStorage.setItem('refreshToken', newRefresh);
        }

        processQueue(null, newAccess);
        originalRequest.headers['Authorization'] = `Bearer ${newAccess}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Refresh failed — clear tokens and notify AuthContext
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        delete api.defaults.headers.common['Authorization'];
        if (onForceLogout) {
          onForceLogout();
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Add standard API endpoints
const apiEndpoints = {
  clients: {
    list: () => api.get('/api/clients/'),
    get: (id) => api.get(`/api/clients/${id}/`),
    create: (data) => api.post('/api/clients/', data),
    update: (id, data) => api.put(`/api/clients/${id}/`, data),
    patch: (id, data) => api.patch(`/api/clients/${id}/`, data),
    delete: (id, cascade = 'keep') => api.delete(`/api/clients/${id}/?cascade=${cascade}`),
    deletePreview: (id) => api.get(`/api/clients/${id}/delete-preview/`),
    previewEmail: (data) => api.post('/api/clients/preview-email/', data),
    sendWelcomeEmail: (clientId, data) => api.post(`/api/clients/${clientId}/send-welcome-email/`, data),
    availableLawyers: () => api.get('/api/clients/available-lawyers/'),
  }
};

// Add the endpoints to the api object
Object.assign(api, apiEndpoints);

// Academy (student experience) — all endpoints under /api/academy/, JWT auth,
// student role required. Mirrors the AuthAPI/researchAPI style so call sites
// stay clean. Quota exhaustion surfaces as HTTP 403 with { detail }.
export const academyAPI = {
  // Curriculum (global, read-only)
  getAreas: () => api.get('/api/academy/areas/'),
  getTopics: (areaId) => api.get('/api/academy/topics/', { params: areaId ? { area: areaId } : {} }),

  // Profile / usage / progress
  getProfile: () => api.get('/api/academy/profile/'),
  updateProfile: (data) => api.patch('/api/academy/profile/', data),
  getUsage: () => api.get('/api/academy/usage/'),
  getProgress: () => api.get('/api/academy/progress/'),

  // Study tutor
  explain: (data) => api.post('/api/academy/tutor/explain/', data),
  getTutorSessions: () => api.get('/api/academy/tutor-sessions/'),

  // Quizzes
  generateQuiz: (data) => api.post('/api/academy/quizzes/generate/', data),
  getQuiz: (id) => api.get(`/api/academy/quizzes/${id}/`),
  submitQuiz: (id, answers) => api.post(`/api/academy/quizzes/${id}/submit/`, { answers }),
  getQuizAttempts: () => api.get('/api/academy/quiz-attempts/'),

  // Mock trials
  startTrial: (data) => api.post('/api/academy/trials/start/', data),
  getTrials: () => api.get('/api/academy/trials/'),
  getTrial: (id) => api.get(`/api/academy/trials/${id}/`),
  trialTurn: (id, content) => api.post(`/api/academy/trials/${id}/turn/`, { content }),
  examineWitness: (id, question) => api.post(`/api/academy/trials/${id}/examine-witness/`, { question }),
  concludeTrial: (id) => api.post(`/api/academy/trials/${id}/conclude/`),
};

// Legal Library — platform-wide hosted primary legal sources (Constitution,
// Acts, SIs, etc.). All endpoints under /api/library/, JWT auth. Browsing/reading
// is open to every authenticated role; uploading is admin-only (server enforces).
export const libraryAPI = {
  getDocuments: (params) => api.get('/api/library/documents/', { params }),
  getDocument: (id) => api.get(`/api/library/documents/${id}/`),
  // Ask the AI to explain a selected passage. Server routes the response mode
  // (tutor vs professional) by role; students may hit a daily quota -> 403 { detail }.
  explainSection: (docId, passage) => api.post(`/api/library/documents/${docId}/explain/`, { passage }),
  // formData must be a FormData instance — leave Content-Type unset so the
  // browser/axios attaches the multipart boundary automatically.
  uploadDocument: (formData) => api.post('/api/library/documents/upload/', formData),
};

export const researchAPI = {
  analyze: (document) => api.post('/research/analyze', { document }),
  search: (query) => api.post('/research/research', { query }),
  getModels: () => api.get('/research/models'),
  getSettings: () => api.get('/research/settings'),
  updateSettings: (settings) => api.post('/admin/llm-settings', settings),
};

// Add auth API methods
export const AuthAPI = {
  login: async (credentials) => {
    try {
      console.log('AuthAPI.login: Making request to /api/accounts/proxy-login/ with:', credentials);
      const response = await api.post('/api/accounts/proxy-login/', credentials);
      console.log('AuthAPI.login: Response status:', response.status);
      console.log('AuthAPI.login: Response data:', response.data);
      return response;
    } catch (error) {
      console.error('AuthAPI.login: Error occurred:', error);
      console.error('AuthAPI.login: Error response:', error.response?.data);
      console.error('AuthAPI.login: Error status:', error.response?.status);
      throw error;
    }
  },
  getCurrentUser: () => api.get('/api/accounts/me/'),
  register: (userData) => api.post('/api/accounts/register/', userData),
  resetPassword: (data) => api.post('/api/accounts/reset-password/', data),
  verifyToken: (token) => api.post('/api/accounts/verify-token/', { token }),
  refreshToken: async (refreshToken) => {
    try {
      const response = await api.post('/api/accounts/refresh/', {
        refresh: refreshToken
      });
      return response.data;
    } catch (error) {
      console.error('AuthAPI.refreshToken: Error occurred:', error);
      throw error;
    }
  },
  // Two-Factor Authentication
  verify2FA: (data) => api.post('/api/accounts/verify-2fa/', data),
  resend2FA: (data) => api.post('/api/accounts/resend-2fa/', data),
  toggle2FA: (data) => api.post('/api/accounts/toggle-2fa/', data),
};

// Export api as both default and named export for backward compatibility
export { api };
export default api;
